/**
 * Lead ops timeline — processing activity for automated pricing and confirmations.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import {
  Refresh,
  CheckCircle,
  ErrorOutline,
  WarningAmber,
  InfoOutlined,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { format } from "date-fns";
import { getLeadOpsTimeline } from "../services/leadOpsTimelineService.js";
import {
  formatEnrichmentProviderLabel,
  formatOpsEventDetailLines,
} from "../utils/leadOpsTimelineFormat.js";

const TimelineContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  position: "relative",
  paddingLeft: "24px",
});

const TimelineLine = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "8px",
  top: 0,
  bottom: 0,
  width: "2px",
  backgroundColor: theme.palette.divider,
}));

const TimelineItem = styled(Box)({
  position: "relative",
  marginLeft: "8px",
  paddingBottom: "4px",
});

const TimelineDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "eventstatus",
})(({ eventstatus, theme }) => {
  const colors = {
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
  };

  return {
    position: "absolute",
    left: "-20px",
    top: "6px",
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    backgroundColor: colors[eventstatus] || theme.palette.grey[400],
    border: `2px solid ${theme.palette.background.paper}`,
    zIndex: 1,
  };
});

function statusIcon(status) {
  switch (status) {
    case "success":
      return <CheckCircle fontSize="small" color="success" />;
    case "warning":
      return <WarningAmber fontSize="small" color="warning" />;
    case "error":
      return <ErrorOutline fontSize="small" color="error" />;
    default:
      return <InfoOutlined fontSize="small" color="info" />;
  }
}

/**
 * @param {object} props
 * @param {string} props.leadId
 * @param {boolean} [props.embedded] - Render without outer card/header (for drawer)
 */
const LeadOpsTimelinePanel = ({ leadId, embedded = false }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTimeline = useCallback(async () => {
    if (!leadId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getLeadOpsTimeline(leadId, { sort: "asc", limit: 100 });
      if (!result?.success) {
        setError(result?.error || "Failed to load activity timeline");
        setEvents([]);
        return;
      }
      setEvents(result.data?.events || []);
    } catch (err) {
      setError(err?.message || "Failed to load activity timeline");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  const quoteIdFromEvents = events
    .map((event) => event.detail?.quoteId)
    .filter(Boolean)
    .pop();

  const timelineBody = (
    <>
      {!embedded && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
              Processing activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Lead submission, auto-pricing, enrichment, and quote preparation
            </Typography>
          </Box>
          <Tooltip title="Refresh timeline">
            <span>
              <IconButton
                onClick={loadTimeline}
                disabled={loading}
                aria-label="Refresh processing activity"
                size="small"
              >
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {embedded && (
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <Tooltip title="Refresh timeline">
            <span>
              <IconButton
                onClick={loadTimeline}
                disabled={loading}
                aria-label="Refresh processing activity"
                size="small"
              >
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadTimeline}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!loading && !error && events.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No activity recorded yet for this lead.
        </Typography>
      )}

      {!loading && !error && events.length > 0 && (
        <>
          <TimelineContainer>
            <TimelineLine />
            {events.map((event) => {
              const detailLines = formatOpsEventDetailLines(event.detail);
              const providerLabel = formatEnrichmentProviderLabel(event.provider);

              return (
                <TimelineItem key={event.id || `${event.type}-${event.occurredAt}`}>
                  <TimelineDot eventstatus={event.status || "info"} />
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    {statusIcon(event.status)}
                    <Box flex={1}>
                      <Box
                        display="flex"
                        flexWrap="wrap"
                        alignItems="center"
                        gap={1}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          {event.summary}
                        </Typography>
                        {providerLabel && (
                          <Chip
                            label={providerLabel}
                            size="small"
                            variant="outlined"
                            color={
                              event.provider === "fixture" ? "warning" : "default"
                            }
                          />
                        )}
                        {event.category && (
                          <Chip
                            label={event.category}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {event.occurredAt
                          ? format(new Date(event.occurredAt), "MMM d, yyyy HH:mm:ss")
                          : "—"}
                      </Typography>
                      {detailLines.map((line) => (
                        <Typography
                          key={line}
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </TimelineItem>
              );
            })}
          </TimelineContainer>

          {quoteIdFromEvents && (
            <Box mt={2}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(`/quotes/${quoteIdFromEvents}`)}
              >
                Open draft quote
              </Button>
            </Box>
          )}
        </>
      )}
    </>
  );

  if (embedded) {
    return timelineBody;
  }

  return (
    <Card elevation={1}>
      <CardContent sx={{ p: 3 }}>{timelineBody}</CardContent>
    </Card>
  );
};

export default LeadOpsTimelinePanel;
